import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import capitalize from 'lodash/capitalize';
import { CircleQuestion } from 'phx-core';

import {
  Input,
  Output,
  OnInit,
  Injector,
  Component,
  HostBinding,
  EventEmitter,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  Router,
  RouterEvent,
  NavigationEnd,
} from '@angular/router';
import { Location } from '@angular/common';

import { Session } from '@services/session';
import { loadScript } from '@lib/scripts';
import { getLocaleForAdaChatbot } from '@lib/i18n';
import { CompanyDOTIds } from '@services/company-dot-ids';
import { FleetGrowthFeatureFlagService } from '@services/feature-flags/growth';
import { SmartLoadBoardFeatureFlagService } from '@services/feature-flags/smart-load-board';
import { NewSidebarComponent } from '@app/layout/sidebar-2.0/sidebar.component';

import { Role } from '@lib/roles.const';
import { isProduction } from '@lib/environments.const';
import { UPDATE_CREDIT_CARD } from '@lib/customer-support.const';
import { isEmbeddedWebView, hasZuoraAccountNumber } from '@lib/boot-utils';

import { ADA_UNAVAILABLE_MODAL_SELECTOR } from '@lib/utils';
import {
  INavItem,
  IStatsigFlag,
} from '@app/layout/sidebar-2.0/sidebar.constants';

import {
  INavItem as INavItemAdmin2,
  ADMIN_NAV_ITEMS as NEW_ADMIN_NAV_ITEMS,
  DASHBOARD_NAV_ITEMS as NEW_DASHBOARD_NAV_ITEMS,
} from '@app/layout/sidebar-2.0/sidebar.constants';

import { AdminFeatureFlagService } from '@services/feature-flags/admin';
import { DriverAppFeatureFlagService } from '@services/feature-flags/driver-app';
import { MotiveGptFeatureFlagService } from '@services/feature-flags/motive-gpt';
import { EnterprisePlatformFeatureFlagService } from '@app/services/feature-flags/enterprise-platform';
import { FacilityMonitoringFeatureFlagService } from '@services/feature-flags/facility-monitoring';
import { CoachingFeatureFlagService } from '@services/feature-flags/coaching';
import { EntCustomReportingDynamicConfigService } from '@services/dynamic-configs/ent-custom-reporting-enabled';

import 'style-loader!./ada-chatbot.component.deep.scss';

declare global {
  // tslint:disable-next-line: interface-name
  interface Window {
    adaSettings: any;
  }
}

interface IAdaMetaFields {
  email: string;
  eld_sn: string;
  username: string;
  dot_number: string;
  is_driver: boolean;
  company_kt_id: string;
  current_feature_title: string;
  current_feature_category: string;
  has_active_subscription: boolean;
  all_features_name_in_sidebar: string;
  update_payment_method_page_url: string;
}

interface IFeaturesList {
  admin: string[],
  fleetView: string[],
}

@Component({
  selector: 'ada-chatbot',
  templateUrl: './ada-chatbot.component.html',
  styleUrls: ['./ada-chatbot.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdaChatbotComponent implements OnInit {
  public isChatbotEnabled: boolean;
  public isChatBotOpen: boolean = false;
  public isChatbotServiceAvailable: boolean;
  public isChatbotSingleClickUXEnabled: boolean;
  public readonly circleQuestionIcon: string = CircleQuestion;
  @HostBinding('class.light-theme') @Input() public isLightTheme: boolean;
  @Input() public disableClick: boolean;
  @Output() public chatbotUnavailable: EventEmitter<void> = new EventEmitter();

  private dotId: string;
  private adaMetaFields: IAdaMetaFields;
  private routerSubscription: Subscription;
  private featureFlagServices: IStatsigFlag;
  private activeSidebarItems: IFeaturesList = {
    admin: [],
    fleetView: [],
  };
  private ADMIN_NAV_ITEMS: INavItem[];
  private DASHBOARD_NAV_ITEMS: INavItem[];
  private sideBar: any;

  constructor(
    private router: Router,
    private session: Session,
    private location: Location,
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    private companyDOTIds: CompanyDOTIds,
    private adminFeatureFlagService: AdminFeatureFlagService,
    private fgFeatureFlagService: FleetGrowthFeatureFlagService,
    private driverAppFeatureFlagService: DriverAppFeatureFlagService,
    private motiveGptFeatureFlagService: MotiveGptFeatureFlagService,
    private facilityMonitoringFeatureFlagService: FacilityMonitoringFeatureFlagService,
    private enterprisePlatformFeatureFlagService: EnterprisePlatformFeatureFlagService,
    private smartLoadBoardFeatureFlagService: SmartLoadBoardFeatureFlagService,
    private coachingFeatureFlagService: CoachingFeatureFlagService,
    private customReportingDynamicConfigService: EntCustomReportingDynamicConfigService,
  ) {
    this.isChatbotEnabled = fgFeatureFlagService.isChatbotEnabled();
    this.isChatbotSingleClickUXEnabled = fgFeatureFlagService.isChatbotSingleClickUXEnabled();
    this.isChatbotServiceAvailable = false;

    this.sideBar = this.injector.get(NewSidebarComponent);
    this.ADMIN_NAV_ITEMS = NEW_ADMIN_NAV_ITEMS;
    this.DASHBOARD_NAV_ITEMS = NEW_DASHBOARD_NAV_ITEMS;

    const userRole: string = session.getUserRole();
    const assignEldId: string = session.assignedElds().toString();

    this.adaMetaFields = {
      dot_number: '',
      current_feature_title: '',
      current_feature_category: '',
      all_features_name_in_sidebar: '',
      email: this.session.getUserEmail(),
      eld_sn: assignEldId ? assignEldId : '',
      username: this.session.getUserFullName(),
      is_driver: userRole === Role.DRIVER || userRole === Role.UNKNOWN,
      update_payment_method_page_url: window.location.origin + UPDATE_CREDIT_CARD,
      company_kt_id: this.session.getCompany()?.company_id?.toString() ?? '',
      has_active_subscription: hasZuoraAccountNumber(this.session.getCompany()) &&
        this.fgFeatureFlagService.isCreditCardFeatureFlagEnabled(),
    };

    this.featureFlagServices = {
      fgFeatureFlagService,
      adminFeatureFlagService,
      driverAppFeatureFlagService,
      motiveGptFeatureFlagService,
      facilityMonitoringFeatureFlagService,
      enterprisePlatformFeatureFlagService,
      smartLoadBoardFeatureFlagService,
      coachingFeatureFlagService,
      customReportingDynamicConfigService,
    };

    // get the current feature and view name
    this.routerSubscription = router.events
      .subscribe((event: RouterEvent) => {
        if (event instanceof NavigationEnd) {
          this.setFeatureRelatedMetaFields();
          if (window['adaEmbed']) {
            window['adaEmbed'].setMetaFields({
              ...(window.adaSettings ? window.adaSettings : {}),
              ...this.adaMetaFields,
            });
          }
        }
      });
  }

  public ngOnInit(): void {
    if (!isEmbeddedWebView()) {
      const adaEmbedUrl: string = 'https://static.ada.support/embed2.js';
      const data: object = {
        'id': '__ada',
        'data-handle': this.getAdaEnvironment(),
      };

      // need to set this setting before the initialization of ada script
      window.adaSettings = {
        toggleCallback: (isOpen: boolean) => {
          this.isChatBotOpen = isOpen;
          this.cdr.detectChanges();
        },
        eventCallbacks: {
          pendo_login_redirect_product_feedback: () => {
            document.getElementById('pendo-feedback-link').click();
          },
        },
      };

      loadScript(adaEmbedUrl, data).then(res => {
        this.isChatbotServiceAvailable = true;
      }).catch((err: any) => {
        this.isChatbotServiceAvailable = false;
        this.chatbotUnavailable.emit();
      });
    }
  }

  public toggleChatbot(): void {
    if (!this.isChatbotServiceAvailable) {
      document.querySelector(ADA_UNAVAILABLE_MODAL_SELECTOR).classList.toggle('show');
    } else {
      if (!this.isChatBotOpen) this.openChatbot();
      else window['adaEmbed'].toggle();

      this.isChatBotOpen = !this.isChatBotOpen;

      try {
        window['adaEmbed'].setLanguage(getLocaleForAdaChatbot());
      } catch {
        console.error('Chat is not ready to set language');
      }

      this.cdr.detectChanges();
    }
  }

  public openChatbot(): void {
    const companyId: number = this.session.getCompanyProperty('id');

    if (companyId && !this.dotId) {
      this.companyDOTIds.getList(companyId.toString())
      .pipe(take(1))
      .subscribe(data => {
        this.adaMetaFields.dot_number = data.list.length > 0 ? data.list[0].value : '';

        if (this.isAdaScriptEmbedded()) {
          this.setFeatureRelatedMetaFields();
          window['adaEmbed'].setMetaFields({
            ...(window.adaSettings ? window.adaSettings : {}),
            ...this.adaMetaFields,
          });
          window['adaEmbed'].toggle();
        } else {
          document.querySelector(ADA_UNAVAILABLE_MODAL_SELECTOR).classList.toggle('show');
        }
      });
    } else {
      window['adaEmbed'].toggle();
    }
  }

  private setFeatureRelatedMetaFields(): void {
    const featureIndex: number = this.sideBar.currentActiveLinkIndex;
    const isAdminView: boolean = this.location.path().startsWith('/admin/');
    const categoryProperty: string = isAdminView ? 'admin' : 'fleetView';

    this.adaMetaFields.current_feature_category = isAdminView ? 'Admin' : 'FleetView';

    this.adaMetaFields.current_feature_title = this.sideBar.currentFeatureLabel;
    this.adaMetaFields.all_features_name_in_sidebar =
      this.sideBar.sidebarNav.navItems.map((item: INavItemAdmin2) => item.name).join(',');
  }

  private setSidebarFeatureTitles(sideBarItems: INavItem[], sideBarCategory: string): void {
    if (this.activeSidebarItems[sideBarCategory].length === 0) {
      sideBarItems.forEach((item: INavItem) => {
        if (item.shouldNavItemShow(this.session, this.featureFlagServices)) {
          this.activeSidebarItems[sideBarCategory].push(capitalize(item.name));
        }
      });
    }
  }

  private getAdaEnvironment(): string {
    return isProduction() ? 'fleetchat' : 'fleetchat-testing';
  }

  private isAdaScriptEmbedded(): boolean {
    return !!window['adaEmbed'];
  }
}
